import * as R from 'ramda';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
// mui
import { useTheme } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Toggle from '@mui/material/ToggleButton';
import InputLabel from '@mui/material/InputLabel';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import OutlinedInput from '@mui/material/OutlinedInput';
import Visibility from '@mui/icons-material/Visibility';
import InputAdornment from '@mui/material/InputAdornment';
import UploadFileIcon from '@mui/icons-material/CloudUpload';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined';
// components
import DatePicker from '../date-picker';
// constants
import * as C from '../../constants';
// ui
import { Box, Flex, Dropdown } from '../../ui';
// utilities
import * as U from '../../utilities';
// component forms
import {
  CleanWrapper,
  UploadWrapper,
  StyledTextField,
  StyledFormControl,
} from './ui';
////////////////////////////////////////////////

export const ErrorText = ({ fieldType, errorText, errorTextStyles }) => (
  <Typography
    mb={0}
    variant='h5'
    component='p'
    fontSize={13}
    textAlign='left'
    fontWeight={400}
    marginLeft='14px'
    color='error.main'
    letterSpacing={0}
    marginRight='14px'
    marginTop={U.ifElse(R.equals(fieldType, C.FIELD_TYPE_FILE), '-17px', '-50px')}
    sx={{ ...errorTextStyles }}
  >
    {errorText}
  </Typography>
);

export const FormField = props => {
  const {
    children,
    fieldType,
    errorTextStyles,
    notificationText,
  } = props;

  if (R.includes(fieldType, [C.FIELD_TYPE_TEXT, C.FIELD_TYPE_PASSWORD])) return children;

  return (
    <>
      {children}
      {
        notificationText && (
          <ErrorText
            fieldType={fieldType}
            errorText={notificationText}
            errorTextStyles={errorTextStyles}
          />
        )
      }
    </>
  );
};

export const InputAdornmentComponent = props => {
  const { t, passwordShown, setPasswordShown } = props;

  return (
    <InputAdornment position='end'>
      <IconButton
        edge='end'
        aria-label='toggle password visibility'
        onClick={() => setPasswordShown(R.not)}
      >
        {
          passwordShown
            ? <Visibility color='disabled' sx={{ fontSize: 24 }} titleAccess={t('actions.hide')} />
            : <VisibilityOff color='disabled' sx={{ fontSize: 24 }} titleAccess={t('actions.show')} />
        }
      </IconButton>
    </InputAdornment>
  );
};

const FieldComponent = props => {
  const {
    field,
    value,
    error,
    onBlur,
    onChange,
    disabled,
    helperText,
    inputStyles,
    setFieldValue,
  } = props;

  const {
    id,
    fileName,
    required,
    fieldType,
    fieldName,
    // headerText,
    // valueLength,
    // defaultValue,
    dropdownOptions,
    // shouldCustomChange,
  } = field;

  const { t } = useTranslation();

  const theme = useTheme();

  const [passwordShown, setPasswordShown] = useState(false);

  const label = `${t(R.pathOr(null, [C.FIELD_LABEL], field))}${required ? '*' : ''}`;
  const type = U.ifElse(passwordShown, C.FIELD_TYPE_TEXT, C.FIELD_PASSWORD);

  if (R.equals(fieldType, C.FIELD_TYPE_TEXT)) {
    return (
      <StyledTextField
        error={error}
        value={value}
        label={label}
        onBlur={onBlur}
        type={fieldType}
        name={fieldName}
        onChange={onChange}
        disabled={disabled}
        helperText={helperText}
        InputLabelProps={{ shrink: R.or(inputStyles.shrink, false) }}
        sx={{
          ...inputStyles,
          '& input': { height: R.or(inputStyles.inputHeight, '6px')},
        }}
      />
    );
  }

  if (R.equals(fieldType, C.FIELD_TYPE_PASSWORD)) {
    return (
      <StyledTextField
        type={type}
        error={error}
        value={value}
        label={label}
        onBlur={onBlur}
        name={fieldName}
        onChange={onChange}
        disabled={disabled}
        helperText={helperText}
        InputLabelProps={{ shrink: R.or(inputStyles.shrink, false) }}
        sx={{
          ...inputStyles,
          '& input': { height: R.or(inputStyles.inputHeight, '6px')},
        }}
        InputProps={{
          endAdornment: (
            <InputAdornmentComponent
              t={t}
              passwordShown={passwordShown}
              setPasswordShown={setPasswordShown}
            />
          ),
        }}
      />
    );
  }

  if (R.equals(fieldType, C.FIELD_TYPE_TOGGLE)) {
    return (
      <Toggle
        id={id}
        checked={value}
        name={fieldName}
        onChange={onChange}
        disabled={disabled}
      />
    );
  }

  if (R.equals(fieldType, C.FIELD_TYPE_FILE)) {
    return (
      <UploadWrapper mt={4} width={inputStyles.width || '350px'}>
        <input
          value=''
          type='file'
          title={R.pathOr('', [C.FIELD_VALUE, C.FIELD_NAME], props)}
          onChange={event => setFieldValue(fieldName, R.pathOr(null, ['currentTarget', 'files', 0], event))}
        />
        {
          U.isNotNilAndNotEmpty(value)
            ? (
              <Typography
                px={10}
                width='calc(100% - 30px)'
                title={R.pathOr(fileName, [C.FIELD_VALUE, C.FIELD_NAME], props)}
                sx={{
                  fontSize: 16,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  color: t => t.palette.background.black,
                }}
              >
                {R.pathOr(fileName, [C.FIELD_VALUE, C.FIELD_NAME], props)}
              </Typography>
            )
            : (
              <Box
                px={10}
                display='flex'
                justifyContent='center'
                title={t('labels.clickToUploadFile')}
              >
                <UploadFileIcon sx={{ mr: '15px', color: t => t.colors.lime }} />
                <Typography
                  sx={{
                    fontSize: 16,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    fontFamily: 'Urbanist',
                    textOverflow: 'ellipsis',
                    color: t => t.palette.background.black,
                  }}
                >
                  {t('labels.clickToUploadFile')}
                </Typography>
              </Box>
            )
        }
        {
          U.isNotNilAndNotEmpty(value)
          && (
            <CleanWrapper
              onClick={event => {
                event.preventDefault();
                setFieldValue(fieldName, null);
              }}
            >
              <DeleteIcon sx={{ mr: '5px' }} titleAccess={t('actions.remove')} />
            </CleanWrapper>
          )
        }
      </UploadWrapper>
    );
  }

  if (R.equals(fieldType, C.FIELD_TYPE_DROPDOWN)) {
    const options = dropdownOptions.map(option => ({
      [C.FIELD_NAME]: t(option[C.FIELD_LABEL]),
      [C.FIELD_VALUE]: option[C.FIELD_VALUE],
    }));

    return (
      <StyledFormControl
        sx={{
          width: R.or(inputStyles.width, '100%'),
          '.MuiOutlinedInput-input': {
            backgroundColor: t => t.colors.bgcolor,
          },
          '.Mui-disabled': {
            'WebkitTextFillColor': t => disabled && `${t.palette.background.lightGrey} !important`,
          },
        }}
      >
        <InputLabel id='SelectField-label'>{label}</InputLabel>
        <Dropdown
          error={error}
          value={value}
          onBlur={onBlur}
          type={fieldType}
          name={fieldName}
          disabled={disabled}
          onChange={onChange}
          defaultValue={value}
          helperText={helperText}
          labelId='SelectField-label'
          id={R.or(id, 'SelectField')}
          input={<OutlinedInput id='OutlinedInput' label={label} />}
          InputLabelProps={{ shrink: R.or(inputStyles.shrink, false) }}
          sx={{
            height: 40,
            minHeight: 40,
            fontSize: '1rem',
            ...inputStyles,
            '& input': { height: R.or(inputStyles.inputHeight, '6px')},
          }}
        >
          {
          options?.map(({ name, value }) => (
            <MenuItem
              key={value}
              value={value}
              disabled={disabled}
              sx={{
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: t => t.palette.background.secondaryMainColor,
                },
                '&.Mui-selected': {
                  color: t => t.palette.primary.main,
                  backgroundColor: t => t.palette.background.default,
                },
              }}
            >
              <Typography sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {t(name)}
              </Typography>
            </MenuItem>
          ))
        }
        </Dropdown>
      </StyledFormControl>
    );
  }

  if (R.equals(fieldType, C.FIELD_TYPE_DATE_RANGE)) {
    return (
      <DatePicker
        theme={theme}
        label={label}
        value={value}
        name={fieldName}
        type={fieldType}
        disabled={disabled}
        onChange={setFieldValue}
      />
    );
  }
};

const Fieldset = props => {
  const {
    fields,
    values,
    errors,
    touched,
    formName,
    handleBlur,
    handleChange,
    setFieldValue,
    disableFields,
    fieldsetStyles,
    handleCustomChange,
  } = props;

  const fieldSetWrapperStyles = R.or(fieldsetStyles, {});

  return (
    <Flex {...fieldSetWrapperStyles}>
      {
        fields.map(field => {
          const {
            disabled,
            fieldName,
            fieldType,
            fieldStyles,
            forceEnable,
            inputStyles,
            errorStyles,
            shouldCustomChange,
          } = field;

          const errorTextStyles = R.or(errorStyles, {});
          const fieldWrapperStyles = R.or(fieldStyles, {});

          const {
            error,
            notificationType,
          } = U.checkHasFieldError(field, errors, touched);

          return (
            <Box key={`${formName}-${fieldName}`} {...fieldWrapperStyles}>
              <FormField
                fieldType={fieldType}
                errorTextStyles={errorTextStyles}
                notificationText={notificationType && error}
              >
                <FieldComponent
                  field={field}
                  props={props}
                  onBlur={handleBlur}
                  inputStyles={inputStyles}
                  setFieldValue={setFieldValue}
                  value={R.prop(fieldName, values)}
                  helperText={notificationType && error}
                  error={U.isNotNilAndNotEmpty(error) && true}
                  disabled={R.and(R.or(disabled, disableFields), R.not(forceEnable))}
                  onChange={U.ifElse(shouldCustomChange, handleCustomChange, handleChange)}
                />
              </FormField>
            </Box>
          );
        })
      }
    </Flex>
  );
};

export default Fieldset;
